import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import clsx from 'clsx';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { teacherService } from '@/services/teacher';
import type { StudentMonthlyAttendance, StudentRecord } from '@/types';
import type { TeacherDashboardResponse } from '@/types/teacher';
import { useAuth } from '@/hooks/useAuth';
import { resolveUserPhoto } from '@/utils/avatars';

export default function TeacherStudentsPage() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const { data: dashboard } = useQuery<TeacherDashboardResponse>({
    queryKey: ['teacher-dashboard'],
    queryFn: () => teacherService.getDashboard(),
  });
  const assignedClassId = dashboard?.classId ?? user?.assignedClassIds?.[0];
  const hasNoClass = Boolean(dashboard) && !dashboard?.classId;
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(() => format(new Date(), 'yyyy-MM'));
  const monthParts = selectedMonth.split('-');
  const monthValue = Number.parseInt(monthParts[1] ?? '', 10);
  const yearValue = Number.parseInt(monthParts[0] ?? '', 10);

  const { data: students = [], isLoading: isStudentsLoading } = useQuery<StudentRecord[]>({
    queryKey: ['teacher-students', assignedClassId, monthValue, yearValue],
    queryFn: () =>
      teacherService.listStudents(assignedClassId ?? '', {
        month: Number.isFinite(monthValue) ? monthValue : undefined,
        year: Number.isFinite(yearValue) ? yearValue : undefined,
      }),
    enabled: Boolean(assignedClassId),
  });

  useEffect(() => {
    if (!students.length) {
      setSelectedStudentId(null);
      return;
    }

    if (!selectedStudentId || !students.some((student) => student.id === selectedStudentId)) {
      setSelectedStudentId(students[0]?.id ?? null);
    }
  }, [students, selectedStudentId]);

  const { data: monthlyAttendance, isLoading: isMonthlyLoading } = useQuery<StudentMonthlyAttendance | undefined>({
    queryKey: ['teacher-student-monthly', selectedStudentId, monthValue, yearValue],
    queryFn: () =>
      selectedStudentId
        ? teacherService.getStudentMonthlyAttendance(selectedStudentId, {
            month: Number.isFinite(monthValue) ? monthValue : undefined,
            year: Number.isFinite(yearValue) ? yearValue : undefined,
          })
        : Promise.resolve(undefined),
    enabled: Boolean(selectedStudentId),
  });

  const getAttendanceRate = (summary?: { present: number; absent: number; late: number; leave: number }) => {
    if (!summary) return null;
    const total = summary.present + summary.absent + summary.late + summary.leave;
    if (total === 0) return null;
    return Math.round((summary.present / total) * 100);
  };

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return students.filter((student: StudentRecord) => {
      const fullName = `${student.firstName} ${student.lastName}`.toLowerCase();
      return fullName.includes(term) || student.rollNumber.toLowerCase().includes(term);
    });
  }, [students, search]);

  const selectedStudent = useMemo(() => {
    return students.find((s) => s.id === selectedStudentId) || null;
  }, [students, selectedStudentId]);

  if (hasNoClass) {
    return (
      <DashboardShell title="Student Directory" role="teacher">
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-xs text-center space-y-3">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto text-xl font-bold border border-indigo-100">👥</div>
          <h3 className="text-lg font-bold text-slate-900">No Homeroom Assigned</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            You do not have a homeroom class assigned to view student rosters. Please contact the school office.
          </p>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title="Student Directory" role="teacher">
      <div className="space-y-6 pb-12">
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-600">
                {dashboard?.className ?? 'Homeroom Class'}
              </span>
              <span className="text-[11px] font-bold bg-indigo-50 text-indigo-700 px-2.5 py-0.5 rounded-full border border-indigo-100">
                {students.length} Enrolled
              </span>
            </div>
            <h2 className="text-lg font-bold text-slate-900 mt-0.5">Class Student Roster</h2>
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <label className="text-xs font-bold text-slate-600 shrink-0">Month:</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSelectedMonth(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-900 min-h-[40px] focus:ring-2 focus:ring-indigo-500 focus:outline-none w-full sm:w-auto"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 space-y-3">
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs space-y-3">
              <div className="relative">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search student or roll #…"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-base sm:text-sm min-h-[40px] text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:outline-none pl-9"
                />
                <span className="absolute left-3 top-3 text-slate-400 text-sm">🔍</span>
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-2.5 text-xs text-slate-400 hover:text-slate-600 font-bold"
                  >
                    ✕
                  </button>
                )}
              </div>

              {isStudentsLoading ? (
                <div className="p-8 text-center text-xs text-slate-400">Loading student directory…</div>
              ) : !filtered.length ? (
                <div className="p-8 text-center text-xs text-slate-400">
                  {search ? 'No matching students found.' : 'No students found for this class.'}
                </div>
              ) : (
                <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
                  {filtered.map((student) => {
                    const isSelected = student.id === selectedStudentId;
                    const rollShort = student.rollNumber ? student.rollNumber.slice(-2) : '—';
                    const fullName = `${student.firstName} ${student.lastName}`.trim();
                    const rate = getAttendanceRate(student.monthlySummary);
                    return (
                      <button
                        key={student.id}
                        type="button"
                        onClick={() => setSelectedStudentId(student.id)}
                        className={clsx(
                          'w-full text-left p-3.5 sm:p-4 rounded-2xl border transition-all flex items-center justify-between gap-3.5 min-h-[72px]',
                          isSelected
                            ? 'bg-indigo-50/70 border-indigo-200 shadow-xs ring-1 ring-indigo-500/20'
                            : 'bg-white hover:bg-slate-50 border-slate-200',
                        )}
                      >
                        <div className="flex items-center space-x-3.5 min-w-0">
                          {student.photo ? (
                            <div className="w-14 h-14 rounded-2xl overflow-hidden shrink-0 border border-slate-200 bg-slate-100 ring-1 ring-slate-200 shadow-xs">
                              <img
                                src={resolveUserPhoto(student.photo, 'student')}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <span
                              className={clsx(
                                'w-14 h-14 rounded-2xl font-mono text-sm font-black flex items-center justify-center border shrink-0',
                                isSelected
                                  ? 'bg-indigo-600 text-white border-indigo-600'
                                  : 'bg-indigo-50 text-indigo-700 border-indigo-100',
                              )}
                            >
                              {rollShort}
                            </span>
                          )}
                          <div className="min-w-0">
                            <div className="font-bold text-base sm:text-lg text-slate-900 leading-snug truncate">{fullName}</div>
                            <div className="text-xs sm:text-sm text-slate-500 font-mono mt-0.5">Roll #{student.rollNumber}</div>
                          </div>
                        </div>
                        {rate !== null && (
                          <span
                            className={clsx(
                              'px-2.5 py-1 rounded-full text-xs font-bold border shrink-0',
                              rate >= 80
                                ? 'bg-teal-50 text-teal-700 border-teal-200'
                                : rate >= 60
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-rose-50 text-rose-700 border-rose-200',
                            )}
                          >
                            {rate}%
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7 space-y-4">
            {!selectedStudent ? (
              <div className="bg-white p-12 rounded-2xl border border-slate-200 shadow-xs text-center text-slate-400">
                Select a student from the list to view attendance records.
              </div>
            ) : (
              <div className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-200 shadow-xs space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 pb-5">
                  <div className="flex items-center space-x-4">
                    {selectedStudent.photo ? (
                      <div className="w-20 h-20 rounded-2xl sm:rounded-3xl overflow-hidden shrink-0 border border-slate-200 bg-slate-100 ring-2 ring-slate-200 shadow-sm">
                        <img
                          src={resolveUserPhoto(selectedStudent.photo, 'student')}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-2xl sm:rounded-3xl bg-indigo-50 text-indigo-700 font-black text-2xl flex items-center justify-center border border-indigo-100 shrink-0">
                        {selectedStudent.firstName?.charAt(0) || 'S'}
                      </div>
                    )}
                    <div>
                      <h3 className="font-black text-xl sm:text-2xl text-slate-900 leading-tight">
                        {selectedStudent.firstName} {selectedStudent.lastName}
                      </h3>
                      <p className="text-sm text-slate-500 font-medium mt-1">
                        Roll #{selectedStudent.rollNumber} · {dashboard?.className ?? 'Homeroom'}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-bold bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full border border-indigo-100">
                    {format(parseISO(`${selectedMonth}-01`), 'MMMM yyyy')}
                  </span>
                </div>

                {monthlyAttendance?.summary && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-teal-50/70 border border-teal-100 p-3 rounded-xl">
                      <div className="text-[10px] font-bold uppercase text-teal-800">Present</div>
                      <div className="text-xl font-black text-teal-700">{monthlyAttendance.summary.present}</div>
                    </div>
                    <div className="bg-rose-50/70 border border-rose-100 p-3 rounded-xl">
                      <div className="text-[10px] font-bold uppercase text-rose-800">Absent</div>
                      <div className="text-xl font-black text-rose-700">{monthlyAttendance.summary.absent}</div>
                    </div>
                    <div className="bg-amber-50/70 border border-amber-100 p-3 rounded-xl">
                      <div className="text-[10px] font-bold uppercase text-amber-800">Late</div>
                      <div className="text-xl font-black text-amber-700">{monthlyAttendance.summary.late}</div>
                    </div>
                    <div className="bg-slate-100 border border-slate-200 p-3 rounded-xl">
                      <div className="text-[10px] font-bold uppercase text-slate-700">Leave</div>
                      <div className="text-xl font-black text-slate-800">{monthlyAttendance.summary.leave}</div>
                    </div>
                  </div>
                )}

                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="p-3 bg-slate-50/80 border-b border-slate-200 flex items-center justify-between text-xs font-bold text-slate-700">
                    <span>Monthly Attendance Log</span>
                    <span className="text-slate-400 font-normal">
                      {monthlyAttendance?.days?.length ?? 0} Recorded Days
                    </span>
                  </div>

                  {isMonthlyLoading ? (
                    <div className="p-8 text-center text-xs text-slate-400">Loading student attendance log…</div>
                  ) : !monthlyAttendance?.days?.length ? (
                    <div className="p-8 text-center text-xs text-slate-400">
                      No attendance logged for this student in {format(parseISO(`${selectedMonth}-01`), 'MMMM yyyy')}.
                    </div>
                  ) : (
                    <div className="max-h-[360px] overflow-y-auto">
                      <table className="min-w-full divide-y divide-slate-100 text-xs">
                        <thead className="bg-slate-100 text-slate-600 font-bold uppercase tracking-wider sticky top-0">
                          <tr>
                            <th className="px-3.5 py-2.5 text-left">Date</th>
                            <th className="px-3.5 py-2.5 text-right">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {monthlyAttendance.days.map((day) => (
                            <tr key={day.date} className="hover:bg-slate-50/80 transition-colors">
                              <td className="px-3.5 py-2.5 text-slate-800 font-semibold">
                                {format(parseISO(day.date), 'EEE, MMM d, yyyy')}
                              </td>
                              <td className="px-3.5 py-2.5 text-right">
                                <span
                                  className={clsx(
                                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold border',
                                    day.status === 'present' && 'bg-teal-50 text-teal-700 border-teal-200',
                                    day.status === 'absent' && 'bg-rose-50 text-rose-700 border-rose-200',
                                    day.status === 'late' && 'bg-amber-50 text-amber-700 border-amber-200',
                                    day.status === 'leave' && 'bg-slate-100 text-slate-700 border-slate-200',
                                    !day.status && 'bg-slate-50 text-slate-400 border-slate-200',
                                  )}
                                >
                                  {day.status ? day.status.toUpperCase() : '—'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
